import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiClient } from '../../../services/apiClient';
import { hasPerm } from '../../../utils/permissions';
import { CommunityComment, CommunityPost, CommunityReaction, User } from '../../../types';
import { AdminView, UserRole } from '../../../enums';
import { MessageSquare, Loader2, AlertCircle, Plus, Tag as TagIcon, X, Pencil, Trash2, ChevronUp } from 'lucide-react';

interface CommunityViewProps {
  currentUser: User;
  hasNewCommunityActivity?: boolean;
  onCommunityActivityFlagChange?: (value: boolean) => void;
}

type PostState = CommunityPost & {
  comments?: CommunityComment[];
  commentsCursor?: string | null;
  commentsLoading?: boolean;
  feedback?: CommunityReaction[];
  feedbackLoading?: boolean;
};

const formatRelativeTime = (value?: string) => {
  if (!value) return 'Just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = date.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const minutes = Math.round(diffMs / 60_000);
  const hours = Math.round(diffMs / 3_600_000);
  const days = Math.round(diffMs / 86_400_000);
  const weeks = Math.round(diffMs / 604_800_000);
  const months = Math.round(diffMs / 2_592_000_000);
  const years = Math.round(diffMs / 31_536_000_000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  if (Math.abs(days) < 7) return rtf.format(days, 'day');
  if (Math.abs(weeks) < 5) return rtf.format(weeks, 'week');
  if (Math.abs(months) < 12) return rtf.format(months, 'month');
  return rtf.format(years, 'year');
};

const isNotFound = (err: any) => {
  const msg = (err?.message || '').toString();
  return msg.includes('404');
};

const markdownComponents: any = {
  h1: ({node, ...props}: any) => <h1 className="text-2xl font-bold mb-4 mt-6 text-zinc-900" {...props} />,
  h2: ({node, ...props}: any) => <h2 className="text-xl font-bold mb-3 mt-5 text-zinc-900" {...props} />,
  h3: ({node, ...props}: any) => <h3 className="text-lg font-bold mb-2 mt-4 text-zinc-900" {...props} />,
  p: ({node, ...props}: any) => <p className="mb-4 text-zinc-700 leading-relaxed" {...props} />,
  ul: ({node, ...props}: any) => <ul className="list-disc list-inside mb-4 ml-4 text-zinc-700" {...props} />,
  ol: ({node, ...props}: any) => <ol className="list-decimal list-inside mb-4 ml-4 text-zinc-700" {...props} />,
  li: ({node, ...props}: any) => <li className="mb-1" {...props} />,
  a: ({node, ...props}: any) => <a className="text-teal-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer" {...props} />,
  blockquote: ({node, ...props}: any) => <blockquote className="border-l-4 border-zinc-300 pl-4 italic text-zinc-600 my-4" {...props} />,
  hr: ({node, ...props}: any) => <hr className="my-6 border-zinc-200" {...props} />,
  code: ({node, inline, className, children, ...props}: any) => {
    return inline ? (
      <code className="bg-zinc-100 px-1 py-0.5 rounded text-sm font-mono text-pink-600" {...props}>
        {children}
      </code>
    ) : (
      <pre className="bg-zinc-900 text-zinc-100 p-4 rounded-lg overflow-x-auto mb-4">
        <code className="bg-transparent" {...props}>
          {children}
        </code>
      </pre>
    );
  },
};

export const CommunityView: React.FC<CommunityViewProps> = ({ currentUser, hasNewCommunityActivity, onCommunityActivityFlagChange }) => {
  const [posts, setPosts] = useState<PostState[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [hasNewActivity, setHasNewActivity] = useState(false);
  const [newPost, setNewPost] = useState<{ title: string; body: string; postType: CommunityPost['postType']; tagsText: string; isMarkdown: boolean; visibleToUserIds: string[] }>(
    { title: '', body: '', postType: 'update', tagsText: '', isMarkdown: true, visibleToUserIds: [] }
  );
  const [postFilter, setPostFilter] = useState<'all' | 'update' | 'coming_soon' | 'complete' | 'for_you'>('all');
  const [showPreview, setShowPreview] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [isUpdatesExpanded, setIsUpdatesExpanded] = useState(false);
  const [lastViewedCounts, setLastViewedCounts] = useState<Record<string, number>>({});
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [userOptionsLoading, setUserOptionsLoading] = useState(false);

  const feedbackKey = useMemo(() => `community:lastFeedbackCounts:${currentUser.id}`, [currentUser.id]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(feedbackKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          setLastViewedCounts(parsed);
        }
      }
    } catch {
      // ignore storage errors
    }
  }, [feedbackKey]);

  const updateLastViewedCount = useCallback((postId: string, count: number) => {
    setLastViewedCounts((prev) => {
      const next = { ...prev, [postId]: count };
      try {
        localStorage.setItem(feedbackKey, JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, [feedbackKey]);

  const feedbackCountFor = useCallback((post?: PostState) => {
    if (!post) return 0;
    return post.feedback?.length ?? post.feedbackCount ?? 0;
  }, []);

  const roleLabel = useMemo(() => currentUser.role || 'User', [currentUser.role]);
  const canPost = useMemo(() => currentUser.permissions?.includes(AdminView.COMMUNITY), [currentUser.permissions]);
  const isAdmin = useMemo(() => currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SEO, [currentUser.role]);

  useEffect(() => {
    if (selectedPostId) {
      setIsUpdatesExpanded(false);
    }
  }, [selectedPostId]);

  // Keep track of viewed counts while the section is expanded
  useEffect(() => {
    if (isUpdatesExpanded && selectedPostId) {
      const post = posts.find(p => p.id === selectedPostId);
      if (post) {
        const currentCount = feedbackCountFor(post);
        updateLastViewedCount(selectedPostId, currentCount);
      }
    }
  }, [posts, isUpdatesExpanded, selectedPostId, feedbackCountFor, updateLastViewedCount]);

  const fetchPosts = useCallback(
    async (silent = false) => {
      let cancelled = false;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await apiClient.community.listPosts({ limit: 20 });
        if (cancelled) return;
        setCursor(res.cursor ?? null);
        setPosts((prev) => {
          const incoming = res.items || [];
          if (silent && prev.length > 0) {
            const incomingMap = new Map(incoming.map(p => [p.id, p]));
            const prevIds = new Set(prev.map(p => p.id));
            
            // Update existing posts with new data while preserving local state
            const merged = prev.map(p => {
              const inc = incomingMap.get(p.id);
              if (inc) {
                return {
                  ...p,
                  ...inc,
                  comments: p.comments,
                  commentsCursor: p.commentsCursor,
                  commentsLoading: p.commentsLoading,
                  feedback: p.feedback,
                  feedbackLoading: p.feedbackLoading,
                };
              }
              return p;
            });

            // Add new posts to the top
            const newPosts = incoming.filter(p => !prevIds.has(p.id));
            
            return [...newPosts, ...merged];
          }
          if (!silent) {
            setHasNewActivity(false);
            onCommunityActivityFlagChange?.(false);
          }
          return incoming;
        });
      } catch (err: any) {
        if (!cancelled) {
          if (isNotFound(err)) {
            setPosts([]);
            setCursor(null);
            setError('Community API is not enabled yet. No posts to show.');
          } else {
            setError(err?.message || 'Failed to load community posts.');
          }
        }
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
      return () => {
        cancelled = true;
      };
    },
    [onCommunityActivityFlagChange]
  );

  useEffect(() => {
    fetchPosts(false);
  }, [fetchPosts]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchPosts(true);
    }, 7000);
    return () => clearInterval(interval);
  }, [fetchPosts]);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      setUserOptionsLoading(true);
      try {
        if (!currentUser || !hasPerm(currentUser, 'USERS')) {
          setUserOptions([]);
          return;
        }
        const list = await apiClient.users.list();
        if (cancelled) return;
        setUserOptions(list || []);
      } catch (err) {
        console.warn('Failed to load users for targeting', err);
      } finally {
        if (!cancelled) setUserOptionsLoading(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await apiClient.community.listPosts({ cursor, limit: 20 });
      setPosts((prev) => [...prev, ...(res.items || [])]);
      setCursor(res.cursor ?? null);
    } catch (err: any) {
      if (isNotFound(err)) {
        setError('Community API is not enabled yet.');
        setCursor(null);
      } else {
        setError(err?.message || 'Failed to load more posts.');
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const ensureFeedback = async (postId: string, silent = false) => {
    const target = posts.find((p) => p.id === postId);
    if (!target || (target.feedbackLoading && !silent)) return;
    
    if (!silent) {
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, feedbackLoading: true } : p)));
    }

    try {
      const res = await apiClient.community.listFeedback(postId);
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, feedback: res.items || [], feedbackLoading: false, feedbackCount: res.items?.length ?? p.feedbackCount } : p)));
    } catch (err: any) {
      if (!silent) setError(err?.message || 'Failed to load feedback.');
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, feedbackLoading: false } : p)));
    }
  };

  const ensureComments = async (postId: string, append = false, silent = false) => {
    const target = posts.find((p) => p.id === postId);
    if (!target || (target.commentsLoading && !silent)) return;
    
    if (!silent) {
      setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, commentsLoading: true } : p));
    }

    try {
      const res = await apiClient.community.listComments(postId, { cursor: append ? target.commentsCursor || undefined : undefined, limit: 10 });
      setPosts((prev) => prev.map((p) => {
        if (p.id !== postId) return p;
        // If silent refresh (not append), we might want to merge or replace. 
        // For simplicity, if silent and not append, we replace the top list but keep existing if we want to avoid jumpiness.
        // But here we just replace if not append.
        const existing = append ? (p.comments || []) : [];
        return { ...p, comments: [...existing, ...(res.items || [])], commentsCursor: res.cursor ?? null };
      }));
    } catch (err: any) {
      if (!silent) setError(err?.message || 'Failed to load comments.');
    } finally {
      setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, commentsLoading: false } : p));
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (selectedPostId) {
      ensureComments(selectedPostId);
      ensureFeedback(selectedPostId);

      interval = setInterval(() => {
        ensureFeedback(selectedPostId, true);
        // We can also poll comments, but be careful about scroll position if list changes.
        // For now, let's just poll feedback as requested.
        // ensureComments(selectedPostId, false, true); 
      }, 5000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPostId]);

  const submitFeedback = async (postId: string) => {
    const draft = feedbackDrafts[postId]?.trim();
    if (!draft) return;
    const target = posts.find((p) => p.id === postId);
    if (!target || target.authorId !== currentUser.id) return;

    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, feedbackLoading: true } : p)));
    try {
      const res = await apiClient.community.addFeedback(postId, { message: draft });
      setPosts((prev) => prev.map((p) => {
        if (p.id !== postId) return p;
        const nextFeedback = [...(p.feedback || []), res];
        return {
          ...p,
          feedback: nextFeedback,
          feedbackCount: nextFeedback.length,
          feedbackLoading: false,
        };
      }));
      setFeedbackDrafts((prev) => ({ ...prev, [postId]: '' }));
    } catch (err: any) {
      setError(err?.message || 'Failed to add feedback.');
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, feedbackLoading: false } : p)));
    }
  };

  const submitComment = async (postId: string) => {
    const draft = drafts[postId]?.trim();
    if (!draft) return;
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, commentsLoading: true } : p));
    try {
      const newComment = await apiClient.community.addComment(postId, { body: draft });
      setPosts((prev) => prev.map((p) => {
        if (p.id !== postId) return p;
        const nextComments = [newComment, ...(p.comments || [])];
        return {
          ...p,
          comments: nextComments,
          commentsCursor: p.commentsCursor || null,
          commentCount: (p.commentCount || 0) + 1,
        };
      }));
      setDrafts((prev) => ({ ...prev, [postId]: '' }));
    } catch (err: any) {
      setError(err?.message || 'Failed to post comment.');
    } finally {
      setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, commentsLoading: false } : p));
    }
  };

  const handleCreateOrUpdatePost = async () => {
    const trimmedTitle = newPost.title.trim();
    const hasBodyContent = newPost.body.trim();
    if (!trimmedTitle || !hasBodyContent) {
      setError('Title and body are required.');
      return;
    }
    setCreating(true);
    setError(null);
    const tags = newPost.tagsText
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    try {
      if (editingPostId) {
        const updated = await apiClient.community.updatePost(editingPostId, {
          title: trimmedTitle,
          body: newPost.body,
          postType: newPost.postType,
          isMarkdown: newPost.isMarkdown,
          tags,
          visibleToUserIds: newPost.visibleToUserIds,
        });
        setPosts((prev) => prev.map((p) => {
          if (p.id !== editingPostId) return p;
          return {
            ...p,
            ...updated,
            feedback: p.feedback,
            feedbackCount: p.feedbackCount,
            commentCount: p.commentCount,
            comments: p.comments,
            commentsCursor: p.commentsCursor,
            feedbackLoading: p.feedbackLoading,
          };
        }));
      } else {
        const created = await apiClient.community.createPost({
          title: trimmedTitle,
          body: newPost.body,
          postType: newPost.postType,
          isMarkdown: newPost.isMarkdown,
          tags,
          visibleToUserIds: newPost.visibleToUserIds,
        });
        setPosts((prev) => [created, ...prev]);
      }
      setIsCreating(false);
      setEditingPostId(null);
      setNewPost({ title: '', body: '', postType: 'update', tagsText: '', isMarkdown: true, visibleToUserIds: [] });
      setShowPreview(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to save post.');
    } finally {
      setCreating(false);
    }
  };

  const handleStartEdit = (post: PostState) => {
    setEditingPostId(post.id);
    setNewPost({
      title: post.title,
      body: post.body,
      postType: post.postType,
      tagsText: (post.tags || []).join(', '),
      isMarkdown: Boolean(post.isMarkdown),
      visibleToUserIds: post.visibleToUserIds || [],
    });
    setIsCreating(true);
  };

  const handleDelete = async (postId: string) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await apiClient.community.deletePost(postId);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (err: any) {
      setError(err?.message || 'Failed to delete post.');
    }
  };

  const toggleTargetUser = (userId: string) => {
    setNewPost((prev) => {
      const exists = prev.visibleToUserIds.includes(userId);
      const nextIds = exists ? prev.visibleToUserIds.filter((id) => id !== userId) : [...prev.visibleToUserIds, userId];
      return { ...prev, visibleToUserIds: nextIds };
    });
  };

  const resolveUserName = (userId: string) => userOptions.find((u) => u.id === userId)?.name || userId;

  const renderTags = (tags?: string[]) => {
    if (!tags || tags.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {tags.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold">
            <TagIcon className="w-2.5 h-2.5" /> {tag}
          </span>
        ))}
      </div>
    );
  };

  const renderListItem = (post: PostState) => {
    const statusLabel = post.postType === 'coming_soon' ? 'Coming Soon' : post.postType === 'complete' ? 'Complete' : 'Update';
    const pillClass = post.postType === 'coming_soon'
      ? 'bg-blue-100 text-blue-700 border-blue-200'
      : post.postType === 'complete'
        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
        : 'bg-amber-100 text-amber-700 border-amber-200';
    const isMine = post.authorId === currentUser.id;
    const isTargeted = !!post.visibleToUserIds && post.visibleToUserIds.includes(currentUser.id);

    return (
      <div 
        key={post.id} 
        onClick={() => {
          setSelectedPostId(post.id);
          setIsUpdatesExpanded(false);
        }}
        className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-teal-200 transition-all cursor-pointer group"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
              {(post.authorName || 'Admin').charAt(0).toUpperCase()}
            </div>
            
            <div>
              <h3 className="text-base font-bold text-zinc-900 group-hover:text-teal-700 transition-colors line-clamp-1">{post.title}</h3>
              <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                <span className="font-medium text-zinc-700">{post.authorName || 'Admin'}</span>
                <span>•</span>
                <span>{formatRelativeTime(post.createdAt)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase border ${pillClass}`}>
              {statusLabel}
            </span>
            {isMine && (
              <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase border bg-zinc-100 text-zinc-700 border-zinc-200">
                Me
              </span>
            )}
            {!isMine && isTargeted && (
              <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase border bg-teal-50 text-teal-700 border-teal-200">
                For You
              </span>
            )}
            
            <div className="flex items-center gap-3 text-zinc-400 pl-2 border-l border-zinc-100">
              <div className="flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4" /> 
                <span className="text-xs font-medium">{post.commentCount ?? (post.comments?.length || 0)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4" />
                <span className="text-xs font-medium">{feedbackCountFor(post)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDetailModal = () => {
    const post = posts.find(p => p.id === selectedPostId);
    if (!post) return null;
    const statusLabel = post.postType === 'coming_soon' ? 'Coming Soon' : post.postType === 'complete' ? 'Complete' : 'Update';
    const pillClass = post.postType === 'coming_soon'
      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm'
      : post.postType === 'complete'
        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-sm'
        : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm';
    const isMine = post.authorId === currentUser.id;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm">
        <div className="absolute inset-0 bg-black/60 transition-opacity" onClick={() => setSelectedPostId(null)}></div>
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[80vh] flex overflow-hidden modal-enter">
          <button 
            onClick={() => setSelectedPostId(null)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white text-zinc-500 hover:text-zinc-900 shadow-sm backdrop-blur-sm transition-all"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col md:flex-row w-full h-full">
            {/* LEFT COLUMN: Post (Top) */}
            <div className="w-full md:w-7/12 h-full flex flex-col border-r border-zinc-200 relative">
              
              {/* Post Content - Flex Grow to take available space */}
              <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-white relative">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                      {(post.authorName || 'Admin').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-bold text-lg text-zinc-900">{post.authorName || 'Admin'}</div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span>{formatRelativeTime(post.createdAt)}</span>
                        <span>•</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${pillClass}`}>
                          {statusLabel}
                        </span>
                        {isMine && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-zinc-100 text-zinc-700 border border-zinc-200">
                            Me
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {(isAdmin || post.authorId === currentUser.id) && (
                    <div className="flex items-center gap-1 mr-8">
                      <button
                        onClick={() => { setSelectedPostId(null); handleStartEdit(post); }}
                        className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-teal-600 transition-colors"
                        title="Edit post"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { if(window.confirm('Delete this post?')) { handleDelete(post.id); setSelectedPostId(null); } }}
                        className="p-2 rounded-lg hover:bg-red-50 text-zinc-500 hover:text-red-600 transition-colors"
                        title="Delete post"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <h1 className="text-2xl font-extrabold text-zinc-900 mb-6 leading-tight">{post.title}</h1>

                {renderTags(post.tags)}

                {post.visibleToUserIds && post.visibleToUserIds.length > 0 ? (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-teal-700 bg-teal-50 border border-teal-100 px-2 py-1 rounded-full">For You</span>
                    {post.visibleToUserIds.map((id) => (
                      <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200 text-[11px] font-semibold">
                        {resolveUserName(id)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-zinc-500">Visible to everyone with Community access.</p>
                )}

                <div className="mt-6">
                  {post.isMarkdown ? (
                    <ReactMarkdown
                      className="community-markdown"
                      remarkPlugins={[remarkGfm]}
                      linkTarget="_blank"
                      skipHtml
                      components={markdownComponents}
                    >
                      {post.body || ''}
                    </ReactMarkdown>
                  ) : (
                    <div className="community-markdown whitespace-pre-wrap">
                      {post.body || ''}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Comments - Full Height */}
            <div className="w-full md:w-5/12 h-full flex flex-col bg-zinc-50">
              <div className="p-4 border-b border-zinc-200 bg-white flex items-center justify-between shadow-sm z-10">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-teal-600" />
                  <h3 className="font-bold text-zinc-900">Comments ({post.commentCount ?? (post.comments?.length || 0)})</h3>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                {post.commentsLoading && post.comments?.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 text-zinc-500">
                    <Loader2 className="w-8 h-8 animate-spin mb-2 text-teal-500" />
                    <span className="text-sm">Loading comments...</span>
                  </div>
                )}

                {(post.comments || []).map((c) => (
                  <div key={c.id} className="bg-white rounded-lg border border-zinc-200 p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 flex items-center justify-center text-zinc-700 font-bold text-xs">
                          {c.userName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-zinc-900">{c.userName}</div>
                          <div className="text-[11px] text-zinc-500">{formatRelativeTime(c.createdAt)}</div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-line">{c.body}</p>
                  </div>
                ))}

                {!post.commentsLoading && (!post.comments || post.comments.length === 0) && (
                  <p className="text-sm text-zinc-500 text-center py-6">No comments yet.</p>
                )}

                {post.commentsCursor && !post.commentsLoading && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => ensureComments(post.id, true)}
                      className="text-sm text-teal-600 hover:text-teal-700 font-semibold"
                    >
                      Load more comments
                    </button>
                  </div>
                )}
              </div>

              {/* Feedback Section - Collapsible (Moved to Right Column) */}
              <div 
                className={`border-t border-zinc-200 bg-zinc-50 transition-all duration-300 ease-in-out flex flex-col shrink-0 ${
                  isUpdatesExpanded ? 'h-80' : 'h-12'
                }`}
                onClick={() => {
                  if (!isUpdatesExpanded) {
                    setIsUpdatesExpanded(true);
                    updateLastViewedCount(post.id, feedbackCountFor(post));
                  }
                }}
              >
                <div className="h-12 px-4 border-b border-zinc-200 bg-white flex items-center justify-between shadow-sm shrink-0 cursor-pointer hover:bg-zinc-50"
                     onClick={(e) => {
                       if (isUpdatesExpanded) {
                         e.stopPropagation();
                         setIsUpdatesExpanded(false);
                       }
                     }}
                >
                  <div className="flex items-center gap-2">
                    <div className={`transition-transform duration-300 ${isUpdatesExpanded ? 'rotate-180' : ''} ${!isUpdatesExpanded && (feedbackCountFor(post) > (lastViewedCounts[post.id] || 0)) ? 'animate-bounce' : ''}`}>
                       <ChevronUp className="w-4 h-4 text-teal-600" />
                    </div>
                    <h3 className="font-bold text-zinc-900 text-sm">
                      Recent updates ({feedbackCountFor(post)})
                    </h3>
                    {!isUpdatesExpanded && (feedbackCountFor(post) > (lastViewedCounts[post.id] || 0)) && (
                       <div className="flex items-center gap-2 ml-3 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-100 shadow-sm">
                         <span className="relative flex h-2 w-2">
                           <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                           <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                         </span>
                         <span className="text-xs font-bold text-teal-700">New updates available</span>
                       </div>
                    )}
                  </div>
                  
                  {isUpdatesExpanded && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setIsUpdatesExpanded(false); }}
                      className="p-1 rounded-full hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className={`flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar ${!isUpdatesExpanded ? 'hidden' : ''}`}>
                  {post.feedbackLoading && (!post.feedback || post.feedback.length === 0) && (
                    <div className="flex flex-col items-center justify-center py-4 text-zinc-500">
                      <Loader2 className="w-4 h-4 animate-spin mb-1 text-teal-500" />
                      <span className="text-xs">Loading...</span>
                    </div>
                  )}

                  {(post.feedback || []).map((f) => (
                    <div key={f.id} className="bg-white rounded-md border border-zinc-200 p-2 shadow-sm">
                      <p className="text-xs font-bold text-zinc-800 leading-relaxed whitespace-pre-line">{f.message}</p>
                      <div className="text-[10px] text-zinc-400 mt-1 text-right">{formatRelativeTime(f.createdAt)}</div>
                    </div>
                  ))}

                  {!post.feedbackLoading && (!post.feedback || post.feedback.length === 0) && (
                    <p className="text-xs text-zinc-500 text-center py-4">No updates yet.</p>
                  )}
                </div>

                {isMine && isUpdatesExpanded && (
                  <div className="p-2 border-t border-zinc-200 bg-white flex justify-end">
                    <button
                      onClick={() => setShowFeedbackInput(true)}
                      className="px-3 py-1.5 bg-teal-600 text-white rounded-md text-xs font-semibold hover:bg-teal-700 shadow-sm transition-all"
                    >
                      Add Update
                    </button>
                  </div>
                )}

                {/* Feedback Input Popup */}
                {showFeedbackInput && (
                  <div className="absolute inset-0 z-20 bg-black/10 backdrop-blur-[1px] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl border border-zinc-200 w-full max-w-sm p-4 space-y-3 animate-in fade-in zoom-in duration-200">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-zinc-900 text-sm">Add Update</h3>
                        <button onClick={() => setShowFeedbackInput(false)} className="p-1 rounded-full hover:bg-zinc-100 text-zinc-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <textarea
                        className="w-full p-3 border border-zinc-200 rounded-xl bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        rows={3}
                        placeholder="Share an update..."
                        value={feedbackDrafts[post.id] || ''}
                        onChange={(e) => setFeedbackDrafts((prev) => ({ ...prev, [post.id]: e.target.value }))}
                        disabled={post.feedbackLoading}
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setShowFeedbackInput(false)}
                          className="px-3 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-100 text-sm font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => { submitFeedback(post.id); setShowFeedbackInput(false); }}
                          className="px-4 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed"
                          disabled={post.feedbackLoading || !feedbackDrafts[post.id]?.trim()}
                        >
                          Post
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-zinc-200 bg-white">
                <label className="text-xs font-semibold text-zinc-600">Add a comment</label>
                <textarea
                  className="w-full mt-2 p-3 border border-zinc-200 rounded-xl bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  rows={3}
                  placeholder="Share an update or question"
                  value={drafts[post.id] || ''}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [post.id]: e.target.value }))}
                  disabled={post.commentsLoading}
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={() => submitComment(post.id)}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={post.commentsLoading || !drafts[post.id]?.trim()}
                  >
                    Post Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const filteredPosts = useMemo(() => {
    if (postFilter === 'all') return posts;
    if (postFilter === 'for_you') {
      return posts.filter((p) => (p.visibleToUserIds && p.visibleToUserIds.includes(currentUser.id)) || p.authorId === currentUser.id);
    }
    return posts.filter((p) => p.postType === postFilter);
  }, [postFilter, posts, currentUser.id]);

  const showNewActivity = hasNewCommunityActivity || hasNewActivity;

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <style>{`
        @keyframes modalEnter {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .modal-enter {
          animation: modalEnter 0.2s ease-out forwards;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d4d4d8;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a1a1aa;
        }
        .community-markdown {
          color: #1f2937;
          line-height: 1.6;
          white-space: normal;
          word-break: break-word;
        }
        .community-markdown > *:first-child { margin-top: 0; }
        .community-markdown > * + * { margin-top: 0.9rem; }
        .community-markdown h1 {
          font-size: 1.5rem;
          font-weight: 800;
          margin: 1.25rem 0 0.5rem;
          line-height: 1.25;
        }
        .community-markdown h2 {
          font-size: 1.25rem;
          font-weight: 800;
          margin: 1.1rem 0 0.45rem;
          line-height: 1.3;
        }
        .community-markdown h3 {
          font-size: 1.1rem;
          font-weight: 700;
          margin: 1rem 0 0.4rem;
          line-height: 1.35;
        }
        .community-markdown p {
          margin: 0 0 0.8rem;
        }
        .community-markdown ul,
        .community-markdown ol {
          margin: 0 0 0.9rem 0;
          padding-left: 1.25rem;
          list-style-position: outside;
        }
        .community-markdown ol {
          list-style-type: decimal;
        }
        .community-markdown li {
          margin: 0.35rem 0;
        }
        .community-markdown hr {
          border: 0;
          border-top: 1px solid #e4e4e7;
          margin: 1.25rem 0;
        }
        .community-markdown strong {
          font-weight: 700;
        }
        .community-markdown code {
          background: #f4f4f5;
          padding: 0.1rem 0.35rem;
          border-radius: 0.3rem;
          font-size: 0.95em;
        }
      `}</style>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-teal-800">Community Updates</h2>
          <p className="text-xs text-zinc-600 mt-0.5">Share updates, upcoming plans, and collect quick feedback with reasons.</p>
        </div>
        <div className="flex items-center gap-2">
          {showNewActivity && (
            <button
              onClick={() => {
                setHasNewActivity(false);
                onCommunityActivityFlagChange?.(false);
                fetchPosts(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 text-white border-2 border-teal-400 text-sm font-bold shadow-lg shadow-teal-300/50 hover:shadow-xl transition-all"
              title="New community activity"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse shadow-md"></span>
              <span>New Activity</span>
            </button>
          )}
          {canPost && (
            <button
              onClick={() => {
                setEditingPostId(null);
                setNewPost({ title: '', body: '', postType: 'update', tagsText: '', isMarkdown: true, visibleToUserIds: [] });
                setShowPreview(false);
                setIsCreating(true);
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold shadow-lg shadow-teal-300/50 hover:shadow-xl hover:from-teal-600 hover:to-teal-700 transition-all"
            >
              <Plus className="w-5 h-5" /> New Post
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 bg-zinc-50 p-1 rounded-lg border border-zinc-200">
        {[
          { key: 'all', label: 'All' },
          { key: 'update', label: 'Updates' },
          { key: 'coming_soon', label: 'Coming Soon' },
          { key: 'complete', label: 'Complete' },
          { key: 'for_you', label: 'For You' },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setPostFilter(f.key as typeof postFilter)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${
              postFilter === f.key 
                ? 'bg-white text-teal-600 shadow-sm' 
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isCreating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setIsCreating(false); setEditingPostId(null); setShowPreview(false); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl border-2 border-zinc-200 w-full max-w-lg p-6 space-y-4 animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between pb-3 border-b-2 border-zinc-100">
              <div>
                <h3 className="text-xl font-extrabold text-zinc-900">{editingPostId ? 'Edit Post' : 'Create New Post'}</h3>
                <p className="text-xs text-zinc-600 mt-0.5">Choose who can see this post.</p>
              </div>
              <button onClick={() => { setIsCreating(false); setEditingPostId(null); setShowPreview(false); }} className="p-2 rounded-xl hover:bg-zinc-100 transition-colors">
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-zinc-700 flex items-center gap-2 mb-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                  Title
                </label>
                <input
                  value={newPost.title}
                  onChange={(e) => setNewPost((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 text-sm font-semibold focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all shadow-sm"
                  placeholder="e.g. New Feature Released"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-700 flex items-center gap-2 mb-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                  Message Body
                </label>
                <textarea
                  value={newPost.body}
                  onChange={(e) => setNewPost((prev) => ({ ...prev, body: e.target.value }))}
                  className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all min-h-[150px] max-h-[300px] overflow-y-auto shadow-sm bg-white"
                  placeholder="Write Markdown here..."
                />

                <div className="flex items-center gap-2 mt-2">
                  <input
                    id="markdown-preview-toggle"
                    type="checkbox"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                    className="h-3.5 w-3.5 text-teal-600 border-zinc-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="markdown-preview-toggle" className="text-xs text-zinc-700">
                    Show Markdown Preview
                  </label>
                </div>

                {showPreview && (
                  <div className="mt-2 border border-zinc-200 rounded-lg p-3 bg-zinc-50 max-h-[200px] overflow-y-auto">
                    {newPost.isMarkdown ? (
                      <ReactMarkdown
                        className="community-markdown community-markdown--preview"
                        remarkPlugins={[remarkGfm]}
                        linkTarget="_blank"
                        skipHtml
                        components={markdownComponents}
                      >
                        {newPost.body || ''}
                      </ReactMarkdown>
                    ) : (
                      <div className="community-markdown community-markdown--preview whitespace-pre-wrap text-sm">
                        {newPost.body || ''}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-2">
                  <input
                    id="markdown-flag-toggle"
                    type="checkbox"
                    checked={newPost.isMarkdown}
                    onChange={(e) => setNewPost((prev) => ({ ...prev, isMarkdown: e.target.checked }))}
                    className="h-3.5 w-3.5 text-teal-600 border-zinc-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="markdown-flag-toggle" className="text-xs text-zinc-700">
                    Post using Markdown formatting
                  </label>
                </div>

                <div className="mt-3">
                  <label className="text-xs font-bold text-zinc-700 flex items-center gap-2 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    Who can see this
                  </label>
                  <p className="text-[10px] text-zinc-500">Leave empty to show everyone. Selected users see this in "For You".</p>

                  <div className="flex flex-wrap gap-1.5 mt-1.5 min-h-[24px]">
                    {newPost.visibleToUserIds.length === 0 ? (
                      <span className="text-[10px] text-zinc-500">Everyone</span>
                    ) : (
                      newPost.visibleToUserIds.map((id) => (
                        <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100 text-[10px] font-semibold">
                          {resolveUserName(id)}
                        </span>
                      ))
                    )}
                  </div>

                  <div className="mt-2 max-h-32 overflow-y-auto border border-zinc-200 rounded-lg divide-y divide-zinc-100 bg-white">
                    {userOptionsLoading ? (
                      <div className="flex items-center gap-2 p-2 text-xs text-zinc-500"><Loader2 className="w-3 h-3 animate-spin" /> Loading users...</div>
                    ) : userOptions.length === 0 ? (
                      <div className="p-2 text-xs text-zinc-500">No users available.</div>
                    ) : (
                      userOptions.map((u) => {
                        const checked = newPost.visibleToUserIds.includes(u.id);
                        return (
                          <label key={u.id} className="flex items-center justify-between px-3 py-1.5 hover:bg-zinc-50 cursor-pointer">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleTargetUser(u.id)}
                                className="h-3.5 w-3.5 text-teal-600 border-zinc-300 rounded focus:ring-teal-500"
                              />
                              <div>
                                <div className="text-xs font-semibold text-zinc-800">{u.name}</div>
                                <div className="text-[10px] text-zinc-500">{u.role}</div>
                              </div>
                            </div>
                            {checked && <span className="text-[10px] font-bold text-teal-600">For You</span>}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-zinc-700 flex items-center gap-2 mb-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    Type
                  </label>
                  <select
                    value={newPost.postType}
                    onChange={(e) => setNewPost((prev) => ({ ...prev, postType: e.target.value as CommunityPost['postType'] }))}
                    className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 text-sm font-semibold focus:outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-100 transition-all shadow-sm"
                  >
                    <option value="update">📢 Update</option>
                    <option value="coming_soon">🚀 Coming Soon</option>
                    <option value="complete">✅ Complete</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-zinc-700 flex items-center gap-2 mb-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    Tags
                  </label>
                  <input
                    value={newPost.tagsText}
                    onChange={(e) => setNewPost((prev) => ({ ...prev, tagsText: e.target.value }))}
                    className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-100 transition-all shadow-sm"
                    placeholder="e.g. release, roadmap"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t-2 border-zinc-100">
              <button
                onClick={() => { setIsCreating(false); setEditingPostId(null); setShowPreview(false); }}
                className="px-4 py-2 rounded-lg border-2 border-zinc-200 text-sm font-bold text-zinc-700 hover:bg-zinc-50 transition-all shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrUpdatePost}
                disabled={creating}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold shadow-lg shadow-amber-300/50 hover:shadow-xl hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {creating ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {editingPostId ? 'Saving...' : 'Posting...'}
                  </span>
                ) : (
                  editingPostId ? '💾 Save Changes' : '📤 Publish Post'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 px-5 py-4 rounded-2xl bg-gradient-to-r from-red-50 to-amber-50 text-red-800 border-2 border-red-200 text-sm shadow-lg">
          <div className="p-2 rounded-lg bg-red-200">
            <AlertCircle className="w-5 h-5" />
          </div>
          <span className="font-semibold">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-12 text-base text-zinc-600">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" /> 
          <span className="font-semibold">Loading posts...</span>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-gradient-to-br from-white to-zinc-50 border-2 border-dashed border-zinc-300 rounded-3xl p-12 text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-teal-600" />
          </div>
          <p className="text-zinc-600 text-base font-semibold">No posts yet.</p>
          <p className="text-zinc-500 text-sm mt-1">Add updates and collect feedback here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map(renderListItem)}
          {cursor && (
            <div className="flex justify-center pt-4">
              <button
                onClick={loadMore}
                className="px-6 py-3 rounded-xl border-2 border-zinc-300 bg-white text-base font-bold text-zinc-700 hover:bg-zinc-50 hover:border-zinc-400 shadow-md hover:shadow-lg transition-all"
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  'Load More Posts'
                )}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Detail Modal */}
      {selectedPostId && renderDetailModal()}
    </div>
  );
};
